import { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
  ArrowLeft, BookOpen, Code2, ExternalLink, Star, Github,
  Globe, Server, ChevronRight, Layers, Tag, Calendar, Terminal
} from 'lucide-react'
import { allAlgorithms } from '../data/algorithms'

export default function AlgorithmDetail() {
  const { id } = useParams()
  const algo = allAlgorithms.find((a) => a.id === id)

  useEffect(() => { window.scrollTo(0, 0) }, [id])

  if (!algo) {
    return (
      <div className="pt-32 pb-20 text-center">
        <h1 className="headline-lg text-cream mb-4">Algorithm Not Found</h1>
        <p className="body-text mb-6">The algorithm you're looking for doesn't exist.</p>
        <Link to="/" className="btn-copper">Go Home</Link>
      </div>
    )
  }

  const sections = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'architecture', label: 'Architecture', icon: Layers },
    { id: 'implementation', label: 'Implementation', icon: Code2 },
    { id: 'results', label: 'Results', icon: Star },
    { id: 'frontend', label: 'Frontend', icon: Globe },
    { id: 'backend', label: 'Backend', icon: Server },
    { id: 'references', label: 'References', icon: ExternalLink },
  ].filter((s) => algo.sections[s.id])

  const getAccentColor = () => {
    if (algo.type === 'decode') return 'text-teal-light'
    if (algo.type === 'forge') return 'text-copper'
    return 'text-copper'
  }

  const getBackPath = () => {
    if (algo.type === 'archive') return '/archive'
    if (algo.type === 'decode') return '/decode'
    return '/forge'
  }

  const getBackLabel = () => {
    if (algo.type === 'archive') return 'The Archive'
    if (algo.type === 'decode') return 'The Decode'
    return 'The Forge'
  }

  return (
    <div className="pt-24">
      <Helmet>
        <title>{algo.title} — QuantAlgoLabs</title>
        <meta name="description" content={algo.abstract} />
        <link rel="canonical" href={`https://quantalgolabs.org/algorithm/${algo.id}`} />
        <meta property="og:title" content={algo.title} />
        <meta property="og:description" content={algo.abstract} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{
          JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": algo.title,
            "description": algo.abstract,
            "author": { "@type": "Organization", "name": "QuantAlgoLabs" },
            "publisher": { "@type": "Organization", "name": "QuantAlgoLabs" },
            "datePublished": algo.date,
            "programmingLanguage": algo.language,
            "codeRepository": algo.githubUrl
          })
        }</script>
      </Helmet>

      {/* Hero — Asymmetric, Editorial */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className={`absolute inset-0 bg-${algo.type === 'decode' ? 'teal' : 'copper'}/3 pointer-events-none`} />

        <div className="section-base relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to={getBackPath()}
              className="inline-flex items-center gap-2 text-sm text-cream-dark hover:text-copper transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              Back to {getBackLabel()}
            </Link>

            <div className="flex flex-wrap gap-3 mb-6">
              {algo.tags.map((tag) => (
                <span key={tag} className="text-xs font-mono text-cream-dark uppercase tracking-wider">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="headline-lg text-cream mb-6">{algo.title}</h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-cream-dark mb-8">
              <span className="flex items-center gap-1.5">
                <Star size={14} className="text-copper" />
                {algo.stars.toLocaleString()} stars
              </span>
              <span className="flex items-center gap-1.5">
                <Tag size={14} />
                {algo.language}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                {algo.date}
              </span>
              <span className={`text-xs font-mono ${
                algo.difficulty === 'Expert' ? 'text-red-400/70' :
                algo.difficulty === 'Advanced' ? 'text-yellow-400/70' :
                'text-green-400/70'
              }`}>
                {algo.difficulty}
              </span>
            </div>

            <p className="text-lg text-cream-muted leading-relaxed max-w-3xl">
              {algo.abstract}
            </p>

            {algo.authors && (
              <p className="text-sm font-mono text-cream-dark mt-5 tracking-wide">
                <span className="text-cream-muted">Original Authors:</span> {algo.authors.join(', ')} — {algo.venue}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="section-base">
          <div className="grid lg:grid-cols-[1fr_280px] gap-12 lg:gap-16">
            {/* Main Content */}
            <div className="space-y-16">
              {sections.map((section, i) => {
                const SectionIcon = section.icon
                const content = algo.sections[section.id]

                return (
                  <motion.section
                    key={section.id}
                    id={section.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-display text-2xl font-semibold tracking-tight mb-6 flex items-center gap-3 text-cream">
                      <div className={`w-10 h-10 rounded border border-ink-border flex items-center justify-center ${getAccentColor()}`}>
                        <SectionIcon size={18} />
                      </div>
                      {section.label}
                    </h2>

                    {section.id === 'architecture' && Array.isArray(content) && (
                      <div className="border border-ink-border rounded p-6 lg:p-8 bg-ink-card">
                        <ul className="space-y-4">
                          {content.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <ChevronRight size={18} className={`mt-0.5 shrink-0 ${getAccentColor()}`} />
                              <span className="text-cream-muted leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {section.id === 'references' && Array.isArray(content) && (
                      <div className="space-y-4">
                        {content.map((ref, idx) => (
                          <a
                            key={idx}
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border border-ink-border rounded p-6 hover:border-copper/30 transition-colors duration-300"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-sm flex items-center gap-2 text-cream">
                                {ref.type === 'paper' && <BookOpen size={14} className="text-copper" />}
                                {ref.type === 'code' && <Code2 size={14} className="text-copper" />}
                                {ref.type === 'demo' && <Globe size={14} className="text-copper" />}
                                {ref.type === 'doc' && <BookOpen size={14} className="text-copper" />}
                                {ref.title}
                              </h4>
                              <ExternalLink size={14} className="text-cream-dark" />
                            </div>
                            <p className="text-xs font-mono text-cream-dark break-all">{ref.url}</p>
                          </a>
                        ))}
                      </div>
                    )}

                    {typeof content === 'string' && (
                      <div className="border border-ink-border rounded p-6 lg:p-8 bg-ink-card">
                        <p className="text-cream-muted leading-relaxed">{content}</p>
                      </div>
                    )}
                  </motion.section>
                )
              })}

              {/* Code Preview */}
              <motion.section
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-2xl font-semibold tracking-tight mb-6 flex items-center gap-3 text-cream">
                  <div className={`w-10 h-10 rounded border border-ink-border flex items-center justify-center ${getAccentColor()}`}>
                    <Terminal size={18} />
                  </div>
                  Code Preview
                </h2>
                <div className="border border-ink-border rounded overflow-hidden bg-ink-card">
                  <div className="flex items-center justify-between px-5 py-3 bg-ink-elevated border-b border-ink-border">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                    <span className="text-xs font-mono text-cream-dark uppercase">{algo.language.toLowerCase()}</span>
                  </div>
                  <pre className="p-5 overflow-x-auto font-mono text-sm leading-relaxed">
                    <code className="text-cream-muted">
                      <span className="text-copper">def</span>{' '}
                      <span className="text-cream">main</span>():
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;{`"""${algo.shortTitle} - ${algo.type === 'archive' ? 'Research Paper Implementation' : algo.type === 'decode' ? 'Platform Algorithm Reconstruction' : 'Proprietary Algorithm'}`}
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;{'Repo: ' + algo.githubUrl}
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;{'"""'}
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">from</span>{' '}
                      <span className="text-cream">core.algorithm</span>{' '}
                      <span className="text-copper">import</span>{' '}
                      <span className="text-cream">AlgorithmBase</span>
                      <br />
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">class</span>{' '}
                      <span className="text-cream">{algo.shortTitle.replace(/\s/g, '')}</span>(
                      <span className="text-cream-muted">AlgorithmBase</span>):
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">def</span>{' '}
                      <span className="text-cream">__init__</span>(
                      <span className="text-cream-muted">self</span>):
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">super</span>().
                      <span className="text-cream">__init__</span>()
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-cream-muted">self</span>.
                      <span className="text-cream">name</span> ={' '}
                      <span className="text-copper-light">"{algo.shortTitle}"</span>
                      <br />
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">def</span>{' '}
                      <span className="text-cream">run</span>(
                      <span className="text-cream-muted">self</span>,{' '}
                      <span className="text-cream-muted">data</span>):
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper-light">"""Run the algorithm on input data."""</span>
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-copper">return</span>{' '}
                      <span className="text-cream-muted">self</span>.
                      <span className="text-cream">process</span>(
                      <span className="text-cream-muted">data</span>)
                      <br />
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-cream-dark"># Full implementation available on GitHub</span>
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-cream-dark"># See repository for tests, docs, and examples</span>
                    </code>
                  </pre>
                </div>
              </motion.section>
            </div>

            {/* Sidebar — Sticky */}
            <div className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {/* Table of Contents */}
                <div className="border border-ink-border rounded p-5 bg-ink-card">
                  <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-cream-dark mb-5">
                    Contents
                  </h4>
                  <ul className="space-y-1">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="block px-3 py-2 rounded text-sm text-cream-dark hover:text-cream hover:bg-ink-elevated transition-colors duration-300"
                        >
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Info Card */}
                <div className="border border-ink-border rounded p-5 bg-ink-card">
                  <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-cream-dark mb-5">
                    Algorithm Info
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-cream-dark">Language</span>
                      <span className="font-mono text-xs text-cream">{algo.language}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-cream-dark">Difficulty</span>
                      <span className="text-cream text-sm">{algo.difficulty}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-cream-dark">Added</span>
                      <span className="font-mono text-xs text-cream">{algo.date}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-cream-dark">Stars</span>
                      <span className="font-mono text-xs text-copper">{algo.stars.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* GitHub Card */}
                <a
                  href={algo.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border border-ink-border rounded p-5 hover:border-copper/30 transition-colors bg-ink-card"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded bg-[#24292e] flex items-center justify-center text-white">
                      <Github size={18} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-cream">GitHub Repository</h4>
                      <p className="text-xs text-cream-dark">View source code</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-cream-dark">
                    <span className="flex items-center gap-1.5">
                      <Star size={14} className="text-copper" />
                      {algo.stars.toLocaleString()}
                    </span>
                    <span className="font-mono text-xs">{algo.language}</span>
                  </div>
                </a>

                {/* Tech Stack */}
                <div className="border border-ink-border rounded p-5 bg-ink-card">
                  <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-cream-dark mb-5">
                    Tech Stack
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {algo.tags.map((tag) => (
                      <span key={tag} className="text-xs font-mono text-cream-dark px-2 py-1 rounded bg-ink-elevated border border-ink-border">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
